import styled from '@emotion/styled';
import {
  Card,
  CardContent,
  CardProps,
  Grid,
  Typography,
  Button,
  Box,
  TextField,
  Tabs,
  Tab,
  FormControlLabel,
  Checkbox,
  InputLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  Select,
  MenuItem,
} from '@mui/material';
import { Container } from '@mui/system';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Page } from 'components/Page';
import { ROUTE } from 'route/RouteEnums';
import {
  getRoleByIdAPI,
  getRolePermissionsAPI,
  getActivePermissionsAPI,
  updateRoleAPI,
  createRoleAPI,
  updateRolePermissionsAPI,
} from 'services/roleManagementServices';
import { useEffect } from 'react';
import { roleManagementSchema } from 'utils/schema';
import { yupResolver } from '@hookform/resolvers/yup';
import { toast } from 'react-toastify';
import { LANDING_PAGE_CONFIG } from 'config/landingPageConfig';
import { StyledDialog } from 'theme/index';

const StyledTab = styled(Tab)(() => ({
  textTransform: 'none',
  minHeight: 36,
  padding: '6px 20px',
  borderRadius: 23,
  fontWeight: 500,
  color: '#667085',

  '&.Mui-selected': {
    backgroundColor: '#9F84FF',
    color: '#FFFFFF',
  },
}));

const StyleCard = styled(Card)<CardProps>(({ theme }) => ({
  boxShadow: '0px 4px 16px 0px rgba(0, 0, 0, 0.12)',
  border: '1px solid #EFF4FF',
  borderRadius: 12,
  background: '#FFF',
  margin: '12px 16px',
  position: 'relative',
  zIndex: 99,
  padding: 0,
  '& .MuiTypography-root': {
    position: 'relative',
  },
  '.MuiTypography-h2': {
    fontSize: 20,
  },
  '&.Mui-selected': {
    backgroundColor: '#9F84FF',
    color: '#FFFFFF',
  },
}));

const RoleDetail: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const navigate = useNavigate();

  const isCreateMode = id === 'new';
  const passedRole = location.state?.role as any;

  const [createdDate, setCreatedDate] = useState<string>('N/A');
  const [selectedPermissions, setSelectedPermissions] = useState<Set<string>>(new Set());
  const [selectedTab, setSelectedTab] = useState(0);
  const [isEditing, setIsEditing] = useState(isCreateMode);
  const [showPostSaveModal, setShowPostSaveModal] = useState(false);
  const [initialPermissions, setInitialPermissions] = useState<Set<string>>(new Set());
  const [roleId, setRoleId] = useState<string | null>(id && id !== 'new' ? id : null);

  const {
    register,
    handleSubmit,
    formState: { errors, isDirty },
    reset,
  } = useForm<RoleGeneralForm>({
    resolver: yupResolver(roleManagementSchema),
    mode: 'onChange',
  });

  interface ApiPermission {
    id: number;
    permission: string;
    description: string | null;
    category: string | null;
    restricted: boolean;
    active: boolean;
  }

  interface Section {
    category: string;
    restricted: boolean;
    permissions: {
      id: number;
      key: string;
      description: string;
    }[];
  }

  const [permissionSections, setPermissionSections] = useState<Section[]>([]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setSelectedTab(newValue);
  };

  const [defaultLanding, setDefaultLanding] = useState<string>(
    passedRole?.defaultLandingRoute || ''
  );

  const handlePermissionChange = (key: string) => {
    setSelectedPermissions((prev) => {
      const newSet = new Set(prev);

      if (newSet.has(key)) newSet.delete(key);
      else newSet.add(key);

      const options = getLandingPageOptions(newSet);

      setDefaultLanding((prevLanding) =>
        options.some((o) => o.route === prevLanding) ? prevLanding : ''
      );

      return newSet;
    });
  };

  const onSubmitGeneral = async (data: RoleGeneralForm) => {
    if (!isDirty && !isCreateMode) {
      toast.error(' No changes to save');
      return;
    }

    const payload = {
      name: data.roleName,
      description: data.description,
      // defaultLandingRoute: data.defaultLanding
    };

    try {
      if (isCreateMode) {
        const res = await createRoleAPI(payload);
        setRoleId(res.data.id);
      } else {
        if (!id) return;
        await updateRoleAPI(id, payload);
      }
      setShowPostSaveModal(true);
      setSelectedTab(1);
    } catch (error) {
      console.error('Error saving role:', error);
    }
  };

  const fetchActivePermissions = async () => {
    try {
      const activeResponse = await getActivePermissionsAPI();
      const activePermissions: ApiPermission[] = activeResponse.data;

      const grouped: { [key: string]: Section } = {};

      activePermissions.forEach((perm) => {
        if (!perm.active) return;

        const category = perm.restricted ? 'Restricted Permissions' : perm.category || 'General';

        if (!grouped[category]) {
          grouped[category] = {
            category,
            restricted: perm.restricted,
            permissions: [],
          };
        }

        grouped[category].permissions.push({
          id: perm.id,
          key: perm.permission,
          description: perm.description || '',
        });
      });

      const sectionsArray = Object.values(grouped);

      sectionsArray.sort((a, b) => {
        if (a.restricted === b.restricted) return 0;
        return a.restricted ? 1 : -1;
      });

      setPermissionSections(sectionsArray);
    } catch (error) {
      console.error('Error loading permissions:', error);
    }
  };

  useEffect(() => {
    fetchActivePermissions();

    if (!isCreateMode && id) {
      fetchRoleDetails(id);
    }
  }, [id]);

  const fetchRoleDetails = async (roleIdParam: string) => {
    try {
      setLoading(true);

      const response = await getRoleByIdAPI(roleIdParam);
      const role = response.data;

      reset({
        roleName: role.name,
        description: role.description || '',
        defaultLanding: role.defaultLandingRoute || '',
      });
      setDefaultLanding(role.defaultLandingRoute || '');
      if (role.createdDate) {
        const formattedDate = new Date(role.createdDate).toLocaleString('en-US', {
          month: 'numeric',
          day: 'numeric',
          year: 'numeric',
          hour: 'numeric',
          minute: '2-digit',
          hour12: true,
        });
        setCreatedDate(formattedDate);
      }

      // 1️⃣ Fetch active permissions
      const activeResponse = await getActivePermissionsAPI();
      const activePermissions: ApiPermission[] = activeResponse.data;

      // 2️⃣ Fetch assigned permissions
      const permResponse = await getRolePermissionsAPI(roleIdParam);
      const assignedPermissions: ApiPermission[] = permResponse.data;

      // 3️⃣ Set selected permissions
      const assignedCodes = assignedPermissions.map((p) => p.permission);
      const assignedSet = new Set(assignedCodes);
      setSelectedPermissions(assignedSet);
      setInitialPermissions(new Set(assignedSet));
      const options = getLandingPageOptions(assignedSet);
      console.log('default landing options:', role.defaultLandingRoute, options);
      if (!options.some((o) => o.route === role.defaultLandingRoute)) {
        setDefaultLanding('');
      } else {
        setDefaultLanding(role.defaultLandingRoute);
      }

      // 4️⃣ Group active permissions by category
      const grouped: { [key: string]: Section } = {};

      activePermissions.forEach((perm) => {
        if (!perm.active) return;

        // ✅ Force restricted permissions into separate group
        const category = perm.restricted ? 'Restricted Permissions' : perm.category || 'General';

        if (!grouped[category]) {
          grouped[category] = {
            category,
            restricted: perm.restricted,
            permissions: [],
          };
        }

        grouped[category].permissions.push({
          id: perm.id,
          key: perm.permission,
          description: perm.description || '',
        });
      });

      // Optional: Move Restricted group to bottom
      const dynamicSections = Object.values(grouped).sort((a, b) => {
        if (a.category === 'Restricted Permissions') return 1;
        if (b.category === 'Restricted Permissions') return -1;
        return a.category.localeCompare(b.category);
      });

      setPermissionSections(dynamicSections);

      // 5️⃣ Convert to array
      const sectionsArray = Object.values(grouped);

      // Move restricted sections to bottom
      sectionsArray.sort((a, b) => {
        if (a.restricted === b.restricted) return 0;
        return a.restricted ? 1 : -1;
      });

      setPermissionSections(sectionsArray);
    } catch (error) {
      console.error('Error loading role details:', error);
    } finally {
      setLoading(false);
    }
  };
  interface RoleGeneralForm {
    roleName: string;
    description: string;
    defaultLanding: string;
  }

  const handleSavePermissions = async () => {
    if (!defaultLanding) {
      toast.error('Please select a default landing page');
      return;
    }
    try {
      setLoading(true);

      const hasChanges =
        selectedPermissions.size !== initialPermissions.size ||
        Array.from(selectedPermissions).some((p) => !initialPermissions.has(p)) ||
        defaultLanding !== passedRole?.defaultLandingRoute;

      if (!hasChanges) {
        toast.error('No changes to save');
        return;
      }

      const unrestrictedPermissions: number[] = [];
      permissionSections.forEach((section) => {
        if (!section.restricted) {
          section.permissions.forEach((perm) => {
            if (selectedPermissions.has(perm.key)) {
              unrestrictedPermissions.push(perm.id);
            }
          });
        }
      });
      await updateRolePermissionsAPI(roleId!, {
        permissions: unrestrictedPermissions,
        defaultLandingRoute: defaultLanding,
      });
      setInitialPermissions(new Set(selectedPermissions));
      toast.success('Permissions updated successfully');
      navigate(ROUTE.ADMIN_PRACTICE_MANAGE_ROLES_AND_PERMISSIONS);
    } catch (error) {
      console.error('Error saving permissions:', error);
      toast.error('An error occurred while saving permissions');
    } finally {
      setLoading(false);
    }
  };

  const getLandingPageOptions = (permissions: Set<string>) => {
    const map = new Map<string, { label: string; route: string }>();

    permissions.forEach((perm) => {
      const config = LANDING_PAGE_CONFIG[perm as keyof typeof LANDING_PAGE_CONFIG];
      if (config) {
        config.forEach((option) => map.set(option.route, option));
      }
    });

    return Array.from(map.values());
  };

  return (
    <Page backDir={ROUTE.ADMIN_PRACTICE_MANAGE_ROLES_AND_PERMISSIONS} buttonText='Back'>
      <Container component='main' maxWidth='md'>
        <Grid container spacing={2}>
          {/* Role Name - Large, left-aligned, above the card */}
          <Grid item xs={12}>
            <Typography variant='h5' sx={{ mt: 3, mb: 3 }}>
              Role Details
            </Typography>
          </Grid>

          {/* Main Card with purple top border */}
          <Grid item xs={12}>
            <StyleCard sx={{ borderTop: '12px solid #FFC44D !important' }}>
              <CardContent sx={{ pt: 4, px: 4, pb: 4 }}>
                {/* Tabs and Edit button in the same row */}
                <Grid container alignItems='center' justifyContent='space-between' sx={{ mb: 4 }}>
                  <Grid item>
                    <Tabs
                      value={selectedTab}
                      onChange={handleTabChange}
                      sx={{
                        background: '#F9FAFB',
                        borderRadius: '23px',
                        padding: '4px',
                        minHeight: 'auto',
                        '& .MuiTabs-indicator': {
                          display: 'none',
                        },
                      }}
                    >
                      <StyledTab label='General' />
                      <StyledTab label='Permissions' />
                    </Tabs>
                  </Grid>

                  {!isCreateMode && (
                    <Grid item>
                      <Button
                        variant='outlined'
                        onClick={() => setIsEditing(!isEditing)}
                        sx={{
                          borderRadius: '30px',
                          textTransform: 'none',
                          borderColor: '#9F84FF',
                          color: '#9F84FF',
                          px: 4,
                          py: 1,
                          fontWeight: 500,
                          '&:hover': {
                            borderColor: '#8B6FD6',
                            backgroundColor: 'rgba(159, 132, 255, 0.04)',
                          },
                        }}
                      >
                        {isEditing ? 'Cancel' : 'Edit'}
                      </Button>
                    </Grid>
                  )}
                </Grid>

                {/* General Tab */}
                {selectedTab === 0 && (
                  <Box>
                    <Grid container spacing={4}>
                      {/* Row 1 */}
                      <Grid item xs={12} md={6}>
                        <Box mb={4}>
                          <Typography variant='subtitle2' color='text.secondary' gutterBottom>
                            Name
                          </Typography>
                          <TextField
                            {...register('roleName')}
                            error={!!errors.roleName}
                            helperText={errors.roleName?.message}
                            disabled={!isCreateMode}
                            fullWidth
                            variant='outlined'
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                borderRadius: '12px',
                                backgroundColor: '#FAFAFA',
                              },
                            }}
                          />
                        </Box>
                      </Grid>

                      <Grid item xs={12} md={6}>
                        <Box mb={4}>
                          <Typography variant='subtitle2' gutterBottom>
                            Description
                          </Typography>

                          <TextField
                            {...register('description')}
                            error={!!errors.description}
                            helperText={errors.description?.message}
                            disabled={!isEditing && !isCreateMode}
                            fullWidth
                            minRows={3}
                            variant='outlined'
                            placeholder='None'
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                borderRadius: '12px',
                                backgroundColor: '#FAFAFA',
                              },
                            }}
                          />
                        </Box>
                      </Grid>

                      {/* Row 2 */}
                      <Grid item xs={12} md={6}>
                        <Box mb={4}>
                          <Typography variant='subtitle2' color='text.secondary' gutterBottom>
                            Created
                          </Typography>
                          <TextField
                            value={createdDate}
                            fullWidth
                            variant='outlined'
                            disabled
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                borderRadius: '12px',
                                backgroundColor: '#FAFAFA',
                              },
                            }}
                          />
                        </Box>
                      </Grid>
                    </Grid>

                    {(isEditing || isCreateMode) && (
                      <Box display='flex' justifyContent='flex-end' mt={2}>
                        <Button
                          variant='contained'
                          onClick={handleSubmit(onSubmitGeneral)}
                          sx={{
                            borderRadius: '30px',
                            textTransform: 'none',
                            backgroundColor: '#9F84FF',
                            px: 5,
                            py: 1.2,
                            fontWeight: 500,
                            '&:hover': { backgroundColor: '#8B6FD6' },
                          }}
                        >
                          {isCreateMode ? 'Create Role' : 'Save Changes'}
                        </Button>
                      </Box>
                    )}
                  </Box>
                )}

                {/* Permissions Tab */}
                {selectedTab === 1 && (
                  <Box>
                    <Typography
                      variant='body2'
                      color='text.secondary'
                      sx={{ mb: 3, fontStyle: 'italic' }}
                    >
                      Note: System-level permissions are managed internally and cannot be modified
                      from the UI.
                    </Typography>

                    {permissionSections.map((section) => (
                      <Box
                        key={section.category}
                        mb={4}
                        border='1px solid #EAECF0'
                        borderRadius='12px'
                      >
                        {/* Section Header */}
                        <Box
                          px={3}
                          py={2}
                          sx={{
                            backgroundColor: '#F9FAFB',
                            borderBottom: '1px solid #EAECF0',
                          }}
                        >
                          <Typography variant='h6' fontWeight={600}>
                            {section.category}
                          </Typography>

                          {section.category === 'Restricted Permissions' && (
                            <Typography variant='body2' color='error' sx={{ mt: 1 }}>
                              These permissions are system controlled and cannot be modified.
                            </Typography>
                          )}
                        </Box>

                        {/* Table Header */}
                        <Box px={3} py={2}>
                          {section.permissions.map((perm) => (
                            <Box
                              key={perm.key}
                              mb={2}
                              p={2}
                              border='1px solid #F2F4F7'
                              borderRadius='10px'
                              sx={{
                                backgroundColor: selectedPermissions.has(perm.key)
                                  ? '#F4F1FF'
                                  : '#FFFFFF',
                                transition: '0.2s ease',
                              }}
                            >
                              <FormControlLabel
                                control={
                                  <Checkbox
                                    checked={selectedPermissions.has(perm.key)}
                                    onChange={() => handlePermissionChange(perm.key)}
                                    disabled={section.restricted || (!isEditing && !isCreateMode)}
                                  />
                                }
                                label={
                                  <Box>
                                    <Typography fontWeight={600}>{perm.key}</Typography>

                                    {perm.description && (
                                      <Typography
                                        variant='body2'
                                        color='text.secondary'
                                        sx={{ mt: 0.5 }}
                                      >
                                        {perm.description}
                                      </Typography>
                                    )}
                                  </Box>
                                }
                              />
                            </Box>
                          ))}
                        </Box>
                      </Box>
                    ))}
                    <Box mb={4}>
                      <Typography variant='subtitle2' gutterBottom>
                        Default Landing Page *
                      </Typography>

                      <FormControl fullWidth>
                        <Select
                          value={defaultLanding}
                          onChange={(e) => setDefaultLanding(e.target.value)}
                          displayEmpty
                          disabled={(!isEditing && !isCreateMode) || selectedPermissions.size === 0}
                          sx={{
                            borderRadius: '12px',
                            backgroundColor: '#FAFAFA',
                          }}
                        >
                          <MenuItem value=''>
                            <em>Select Default Landing Page</em>
                          </MenuItem>

                          {getLandingPageOptions(selectedPermissions).map((option) => (
                            <MenuItem key={option.route} value={option.route}>
                              {option.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Box>

                    {(isEditing || isCreateMode) && (
                      <Box display='flex' justifyContent='flex-end'>
                        <Button
                          variant='contained'
                          onClick={handleSavePermissions}
                          sx={{
                            borderRadius: '30px',
                            textTransform: 'none',
                            backgroundColor: '#9F84FF',
                            px: 5,
                            py: 1.2,
                            fontWeight: 500,
                          }}
                        >
                          Save Permissions
                        </Button>
                      </Box>
                    )}
                  </Box>
                )}
              </CardContent>
            </StyleCard>
          </Grid>
        </Grid>
      </Container>
      <StyledDialog
        open={showPostSaveModal}
        onClose={() => setShowPostSaveModal(false)}
        maxWidth='sm'
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 600 }}>Role details saved</DialogTitle>

        <DialogContent>
          <Typography variant='body2' color='text.secondary'>
            The role has been saved successfully. You can now assign permissions for this role.
          </Typography>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            variant='contained'
            onClick={() => setShowPostSaveModal(false)}
            sx={{
              borderRadius: '30px',
              textTransform: 'none',
              backgroundColor: '#9F84FF',
              px: 4,
            }}
          >
            Continue to Permissions
          </Button>
        </DialogActions>
      </StyledDialog>
    </Page>
  );
};

export default RoleDetail;
